import { CELL_HEIGHT, MINUTES_IN_CELL_HEIGHT } from './constants'

const timePartDisplay = (hour: number) =>
  String(hour).padStart(2, '0')

export type DataFromDayY = ReturnType<typeof dataFromDayY>

export const calcMinuteHeight = (
  cellHeight: number,
  minutesInCell: number
) => {
  return Math.ceil(cellHeight) / minutesInCell
}

/**
 * Calculate time-based data based on
 * a y coordinate within a Day Column
 */
export function dataFromDayY({
  y,
  cellHeightPx = CELL_HEIGHT,
  minutesInCell = MINUTES_IN_CELL_HEIGHT,
}: {
  /** The pixel distance from the top of the column */
  y: number
  /**
   * The height of each cell in pixels
   */
  cellHeightPx?: number
  /**
   * The number of minutes in each cell
   */
  minutesInCell?: number
}) {
  const minuteHeightPx = calcMinuteHeight(
    cellHeightPx,
    minutesInCell
  )
  const totalMinutes = y / minuteHeightPx
  const { snapStart: snapTotalMinutes } = snapCalc(
    minutesInCell,
    totalMinutes
  )

  const hour = Math.floor(totalMinutes / 60)
  const minute = totalMinutes - hour * 60

  const snapHour = Math.floor(snapTotalMinutes / 60)
  const snapMinutes = snapTotalMinutes - snapHour * 60

  const nextTotalMinutes = snapTotalMinutes + minutesInCell
  const nextHour = Math.floor(nextTotalMinutes / 60)
  const nextMinutes = nextTotalMinutes - nextHour * 60

  return {
    y: y,
    hour,
    minute,
    snapHour,
    snapMinutes,
    nextHour,
    nextMinutes,
    snap: `${timePartDisplay(snapHour)}:${timePartDisplay(
      snapMinutes
    )}`,
    next: `${timePartDisplay(nextHour)}:${timePartDisplay(
      nextMinutes
    )}`,
  }
}

/**
 * Calculate the start time of an event dropped with
 * its top edge at a y coordinate within a Day Column
 *
 * Unlike `dataFromDayY`, which picks the slot the y
 * coordinate sits inside, this snaps to the nearest slot
 * line. The user lines the dragged event box up with a
 * slot line by eye, so the box top is often a pixel or
 * two above it. Picking the slot it sits inside would
 * move the event a whole slot earlier.
 */
export function droppedStartFromDayY({
  y,
  cellHeightPx = CELL_HEIGHT,
  minutesInCell = MINUTES_IN_CELL_HEIGHT,
}: {
  /** The pixel distance of the event top from the top of the column */
  y: number
  /**
   * The height of each cell in pixels
   */
  cellHeightPx?: number
  /**
   * The number of minutes in each cell
   */
  minutesInCell?: number
}) {
  const minuteHeightPx = calcMinuteHeight(
    cellHeightPx,
    minutesInCell
  )
  const totalMinutes =
    Math.round(y / minuteHeightPx / minutesInCell) *
    minutesInCell
  const hour = Math.floor(totalMinutes / 60)
  return {
    hour,
    minutes: totalMinutes - hour * 60,
  }
}

export function snapCalc(
  step: number,
  minutes: number,
  duration: number = step
) {
  const remainder = minutes % step
  const snapStart = minutes - remainder
  const steps = Math.ceil((remainder + duration) / step)
  const snaps = Array.from({ length: steps }).map(
    (_, i) => snapStart + step * i
  )
  return {
    remainder,
    snapStart,
    snapEnd: snaps[snaps.length - 1],
    steps,
    snaps,
  }
}

/**
 * The time shown on the clock, as minutes since the
 * start of 1970, ignoring the timezone offset
 *
 * Counting this way keeps 24 hours in every day, so the
 * days the clocks change line up with the timetable grid
 */
function clockMinutes(date: Date) {
  const clockTime = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes()
  )
  return clockTime / 60_000
}

/**
 * How many minutes pass on the clock between two dates
 *
 * On the days the clocks change this is not the same as
 * the time that passed. 00:30 to 02:30 is 120 minutes on
 * the clock, even when the clocks go forward and only
 * 60 minutes pass.
 */
export function clockMinutesBetween(start: Date, end: Date) {
  return clockMinutes(end) - clockMinutes(start)
}

/**
 * How many minutes from the startHour, by the time
 * shown on the clock
 *
 * This will always be based on the current locale
 */
export function minutesFromStartHour(
  startHour: number,
  start: Date
) {
  return (
    start.getHours() * 60 + start.getMinutes() - startHour * 60
  )
}
